import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, fmtDate } from "../lib/api";
import { toast } from "sonner";
import {
  Shield, KeyRound, Smartphone, ScrollText, AlertTriangle, Check, X,
} from "lucide-react";

export default function SecurityPage() {
  const qc = useQueryClient();
  const { data: status } = useQuery({
    queryKey: ["sec-pin-status"],
    queryFn: () => api.get("/security/pin/status").then((r) => r.data),
  });
  const { data: me } = useQuery({
    queryKey: ["me"],
    queryFn: () => api.get("/auth/me").then((r) => r.data),
  });
  const { data: activity } = useQuery({
    queryKey: ["sec-activity"],
    queryFn: () => api.get("/security/activity?days=7").then((r) => r.data),
    refetchInterval: 30000,
  });

  const [pin, setPin] = useState("");
  const setPinReq = useMutation({
    mutationFn: () => api.post("/security/pin", { pin }).then((r) => r.data),
    onSuccess: () => {
      toast.success("PIN_SET");
      setPin("");
      qc.invalidateQueries({ queryKey: ["sec-pin-status"] });
    },
    onError: (e) => toast.error("PIN_FAIL", { description: e?.response?.data?.detail || e?.message }),
  });
  const clearPin = useMutation({
    mutationFn: () => api.delete("/security/pin").then((r) => r.data),
    onSuccess: () => {
      toast.success("PIN_CLEARED");
      qc.invalidateQueries({ queryKey: ["sec-pin-status"] });
    },
  });
  // Phase 30.3 — Forgot-PIN e-mail reset (MOCKED delivery).
  const forgotPin = useMutation({
    mutationFn: () => api.post("/security/pin/reset/request").then((r) => r.data),
    onSuccess: (d) => {
      toast.success("RESET_EMAIL_SENT", {
        description: d?.email
          ? `Check ${d.email} for the reset link (MOCKED delivery).`
          : "Check your email for the reset link (MOCKED delivery).",
      });
    },
    onError: (e) =>
      toast.error("RESET_FAIL", { description: e?.response?.data?.detail || e?.message }),
  });

  const [phone, setPhone] = useState("");
  const enable2fa = useMutation({
    mutationFn: () => api.post("/auth/2fa/enable", { phone }).then((r) => r.data),
    onSuccess: () => {
      toast.success("2FA_ENABLED", { description: "SMS 2FA is on (mocked delivery)" });
      qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (e) => toast.error("2FA_FAIL", { description: e?.response?.data?.detail || e?.message }),
  });
  const disable2fa = useMutation({
    mutationFn: () => api.post("/auth/2fa/disable").then((r) => r.data),
    onSuccess: () => {
      toast.success("2FA_DISABLED");
      qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const [confirmToken, setConfirmToken] = useState("");
  const confirmDevice = useMutation({
    mutationFn: (ok) =>
      api.get(`/security/device/confirm?token=${encodeURIComponent(confirmToken)}&ok=${ok}`)
        .then((r) => r.data),
    onSuccess: (d) => {
      toast.success(d.action || "DEVICE_HANDLED");
      qc.invalidateQueries({ queryKey: ["sec-activity"] });
      setConfirmToken("");
    },
    onError: (e) => toast.error("CONFIRM_FAIL", { description: e?.response?.data?.detail || e?.message }),
  });

  return (
    <div data-testid="security-page" className="p-4 space-y-4">
      <header>
        <div className="text-[10px] uppercase tracking-[0.2em] text-rose-300 font-mono inline-flex items-center gap-2">
          <Shield size={12} /> ACCOUNT_SECURITY
        </div>
        <h1 className="font-mono text-2xl tracking-tight mt-1">Security</h1>
        <p className="text-[12px] text-zinc-500 mt-1 max-w-2xl">
          Set an optional 4–6 digit PIN for payments, enable SMS 2FA, and review a 7-day
          IP / device activity log with an <span className="text-amber-300">Is this you?</span> flow.
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* PIN card — Phase 30.3 OPTIONAL, payments work without it */}
        <section className="border border-[#27272A] bg-[#0E0E0E] p-4">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300">
            <KeyRound size={12} /> PAYMENT_PIN · OPTIONAL
          </div>
          <div className="text-[12px] font-mono mt-2 text-white">
            Status: {status?.has_pin
              ? <span className="text-emerald-300">ACTIVE · PIN required on payments</span>
              : <span className="text-zinc-400">NOT_SET · payments work without a PIN</span>}
          </div>
          <input
            data-testid="pin-input"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="4–6 digits"
            inputMode="numeric"
            type="password"
            className="w-full mt-3 h-10 px-2 bg-[#0A0A0A] border border-[#27272A] text-[16px] font-mono tabnum tracking-[0.3em] text-center text-white focus:border-amber-400 outline-none"
          />
          <div className="mt-2 flex gap-2">
            <button
              data-testid="pin-save"
              onClick={() => setPinReq.mutate()}
              disabled={pin.length < 4 || setPinReq.isPending}
              className="flex-1 h-9 border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
            >
              {status?.has_pin ? "UPDATE_PIN" : "ACTIVATE_PIN"}
            </button>
            {status?.has_pin && (
              <button
                data-testid="pin-clear"
                onClick={() => window.confirm("Turn off the payment PIN?") && clearPin.mutate()}
                className="h-9 px-3 border border-zinc-600 text-zinc-300 hover:bg-zinc-800 font-mono uppercase text-[11px] tracking-wider"
              >
                DISABLE
              </button>
            )}
          </div>
          {status?.has_pin && (
            <button
              type="button"
              data-testid="pin-forgot"
              onClick={() => forgotPin.mutate()}
              disabled={forgotPin.isPending}
              className="mt-2 w-full h-9 border border-purple-400/60 text-purple-200 hover:bg-purple-500/10 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
            >
              {forgotPin.isPending ? "SENDING…" : "FORGOT_PIN · EMAIL_ME_A_RESET_LINK"}
            </button>
          )}
          <p className="text-[10px] font-mono text-zinc-500 mt-2 leading-relaxed">
            Optional layer — payment info stays visible and purchases work
            without it. Activate to require the PIN on every subscribe /
            top-up / renew. Wrong 5× → 5-minute lockout. Forgot PIN sends
            a reset link to your registered email (MOCKED delivery).
          </p>
        </section>

        {/* 2FA card */}
        <section className="border border-[#27272A] bg-[#0E0E0E] p-4">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-purple-300">
            <Smartphone size={12} /> SMS_2FA
          </div>
          <div className="text-[12px] font-mono mt-2 text-white">
            Status: {me?.two_factor_enabled
              ? <span className="text-emerald-300">ON · {me?.phone}</span>
              : <span className="text-zinc-500">OFF</span>}
          </div>
          {!me?.two_factor_enabled ? (
            <>
              <input
                data-testid="phone-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+1 555 000 0000"
                className="w-full mt-3 h-10 px-2 bg-[#0A0A0A] border border-[#27272A] text-[13px] font-mono text-white focus:border-purple-400 outline-none"
              />
              <button
                data-testid="enable-2fa"
                onClick={() => enable2fa.mutate()}
                disabled={!phone.trim() || enable2fa.isPending}
                className="w-full mt-2 h-9 border border-purple-400 bg-purple-400 text-black hover:bg-purple-300 font-mono uppercase text-[11px] tracking-wider disabled:opacity-50"
              >
                ENABLE_2FA
              </button>
            </>
          ) : (
            <button
              data-testid="disable-2fa"
              onClick={() => window.confirm("Turn off SMS 2FA?") && disable2fa.mutate()}
              className="w-full mt-3 h-9 border border-rose-400/60 text-rose-300 hover:bg-rose-500/10 font-mono uppercase text-[11px] tracking-wider"
            >
              DISABLE_2FA
            </button>
          )}
          <p className="text-[10px] font-mono text-zinc-500 mt-2 leading-relaxed">
            SMS delivery uses Twilio when keys are provided. Currently{" "}
            <span className="text-amber-300">MOCKED</span> — any 4–8 digit code passes.
          </p>
        </section>

        {/* Device confirmation */}
        <section className="border border-[#27272A] bg-[#0E0E0E] p-4">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-emerald-300">
            <AlertTriangle size={12} /> DEVICE_CONFIRM
          </div>
          <p className="text-[11px] font-mono text-zinc-400 mt-2 leading-relaxed">
            Received an <span className="text-amber-300">&quot;Is this you?&quot;</span>{" "}
            alert token? Paste it here. Confirming YES marks the device known;
            NO revokes all sessions + wipes your PIN.
          </p>
          <input
            data-testid="alert-token-input"
            value={confirmToken}
            onChange={(e) => setConfirmToken(e.target.value)}
            placeholder="alert token"
            className="w-full mt-2 h-9 px-2 bg-[#0A0A0A] border border-[#27272A] text-[11px] font-mono text-white focus:border-amber-400 outline-none"
          />
          <div className="flex gap-2 mt-2">
            <button
              data-testid="alert-yes"
              onClick={() => confirmDevice.mutate(true)}
              disabled={!confirmToken.trim()}
              className="flex-1 h-8 border border-emerald-400 text-emerald-300 hover:bg-emerald-500/10 font-mono uppercase text-[10px] tracking-wider disabled:opacity-50"
            >
              <Check size={11} className="inline mr-1" /> ITS_ME
            </button>
            <button
              data-testid="alert-no"
              onClick={() => confirmDevice.mutate(false)}
              disabled={!confirmToken.trim()}
              className="flex-1 h-8 border border-rose-400 text-rose-300 hover:bg-rose-500/10 font-mono uppercase text-[10px] tracking-wider disabled:opacity-50"
            >
              <X size={11} className="inline mr-1" /> NOT_ME
            </button>
          </div>
        </section>
      </div>

      {/* Activity log */}
      <section className="border border-[#27272A] bg-[#0E0E0E]">
        <div className="px-3 py-2 border-b border-[#27272A] text-[10px] font-mono uppercase tracking-wider text-zinc-500 flex items-center gap-2">
          <ScrollText size={12} /> 7-DAY_ACTIVITY_LOG · {activity?.events?.length ?? 0}
        </div>
        {!activity?.events?.length ? (
          <div className="p-4 text-[11px] font-mono text-zinc-500 text-center">
            NO_ACTIVITY_YET
          </div>
        ) : (
          <ul>
            {activity.events.map((e) => (
              <li
                key={e.id}
                data-testid={`sec-event-${e.id}`}
                className="px-3 py-2 border-b border-[#161616] grid grid-cols-12 gap-2 items-center text-[11px] font-mono"
              >
                <div className="col-span-2 text-zinc-500">
                  {fmtDate(e.at)}
                </div>
                <div className="col-span-2 uppercase text-white">{e.kind}</div>
                <div className="col-span-3 text-zinc-400 truncate">{e.ip || "?"}</div>
                <div className="col-span-4 text-zinc-500 truncate" title={e.user_agent}>
                  {e.user_agent}
                </div>
                <div className="col-span-1 text-right">
                  {e.new_device ? (
                    <span className="text-amber-300">NEW</span>
                  ) : (
                    <span className="text-emerald-400/70">known</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
